import { Box, Button, Grid, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { AppUrl } from "../../config";
import { Query, ResultRecord } from "../../interfaces";
import LeavingFDICDialog from "../LeavingFDICDialog";
import { useLocation } from "react-router-dom";
import { BankFindFinancialsDataViz } from "./BankFindFInancialsDataViz";
import { useBreakpoint } from "../../hooks/useBreakpoint";

const PREFIX = "BankFindFinancials";

const classes = {
  list: `${PREFIX}-list`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.list}`]: {
    marginTop: 0,
    marginBottom: "2rem",
    color: theme.palette.primary.main,
    fontSize: 16,
  },
}));

interface Props {
  institution: ResultRecord;
  appState: Query;
}

export default function BankFindFinancials({ institution, appState }: Props) {
  const location = useLocation();
  const { smallScreen } = useBreakpoint();
  const cert = institution.CERT;
  const risDateMonth = institution.RISDATE.substring(0, 2);
  const risDateDay = institution.RISDATE.substring(3, 5);
  const risDateYear = institution.RISDATE.substring(6, 10);
  const risdate = `${risDateMonth}${risDateDay}${risDateYear}`;
  const risDictLink =
    "https://www7.fdic.gov/DICT/app/templates/Index.html#!/Main";
  const bfsLinks = (
    <>
      <Typography color="primary.main" variant="h6">
        Financial Reports on BankFind Suite
      </Typography>
      <Box mb={smallScreen ? -2 : 0}>
        <ul className={classes.list}>
          <li>Run Standard Reports</li>
          <li>Create Customized Selection</li>
          <li>
            Data Source: <a href={risDictLink}>RIS Dictionary</a>
          </li>
        </ul>
      </Box>
      <Grid container spacing={2} mb={smallScreen ? 2 : 0}>
        <Grid size={6}>
          <Button
            variant="outlined"
            fullWidth
            color="primary"
            id="financials-reporting-btn"
            onClick={() =>
              sessionStorage.setItem(
                "prevUrl",
                `${AppUrl("BankFind")}/details/${cert}${location.search}`,
              )
            }
            sx={(theme) => ({
              fontSize: {
                xs: 10,
                sm: theme.typography.button.fontSize,
              },
            })}
            href={`/bankfind-suite${AppUrl(
              "FinancialReporting",
            )}/report?cert=${cert}`}
          >
            Financial Reporting
          </Button>
        </Grid>
        <Grid size={6}>
          <Button
            variant="outlined"
            fullWidth
            color="primary"
            id="bod-btn"
            sx={(theme) => ({
              fontSize: {
                xs: 10,
                sm: theme.typography.button.fontSize,
              },
            })}
            onClick={() =>
              sessionStorage.setItem(
                "prevUrl",
                `${AppUrl("BankFind")}/details/${cert}${location.search}`,
              )
            }
            href={`/bankfind-suite${AppUrl(
              "SOD",
            )}/branchOffice?cert=${cert}&displayResult`}
          >
            Branch Office Deposits
          </Button>
        </Grid>
      </Grid>
    </>
  );

  const externalLinks = (
    <>
      <Box mb={smallScreen ? -1 : 0}>
        <Typography color="primary.main" variant="h6">
          External Links to Financial Information
        </Typography>
        <Typography className={classes.list}>
          The following links provide the most recent reports submitted.
        </Typography>
      </Box>
      <Grid container spacing={2} sx={{ marginTop: smallScreen ? 0 : "63px" }}>
        <Grid size={6}>
          <LeavingFDICDialog
            webAddress={`https://cdr.ffiec.gov/Public/ViewFacsimileDirect.aspx?ds=call&idType=fdiccert&id=${cert}&date=${risdate}`}
            label={"Latest FFIEC Call Report"}
            noHover
            button
            buttonProps={{ color: "primary", variant: "outlined" }}
          />
        </Grid>
        <Grid size={6}>
          <LeavingFDICDialog
            webAddress={`https://cdr.ffiec.gov/Public/ViewFacsimileDirect.aspx?ds=ubpr&repType=1&idType=fdiccert&id=${cert}`}
            label={"Latest UBPR Report"}
            noHover
            button
            buttonProps={{ color: "primary", variant: "outlined" }}
          />
        </Grid>
      </Grid>
    </>
  );

  return (
    <StyledBox width="100%">
      {smallScreen ? (
        <Box>
          {bfsLinks}
          {externalLinks}
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid size={6}>{bfsLinks}</Grid>
          <Grid size={6}>{externalLinks}</Grid>
        </Grid>
      )}
      {institution.ACTIVE === 1 && (
        <BankFindFinancialsDataViz
          institution={institution}
          appState={appState}
        />
      )}
    </StyledBox>
  );
}
